import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Navbar from "./Navbar";
import Footer from "./Footer";

import {
  Link,
  useNavigate,
  useParams,
  useLocation
} from "react-router-dom";

import Swal from "sweetalert2";

import {
  Country,
  State,
  City,
} from "country-state-city";

import LoginModal, {
  StaticBackdrop,
} from "../components/LoginModal";
import ChatbotWidget from "./chatbot/ChatbotWidget";
import { Helmet } from "react-helmet-async";

const API_BASE = "https://swiftintelli.com/api";

/* ============================================================
   HELPERS
============================================================ */

function prettifySlug(slug) {
  if (!slug) return "";

  return slug
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ============================================================
   DATE FORMAT
============================================================ */

function formatDateDMY(dateStr) {
  if (!dateStr) return "";

  const d = new Date(dateStr);

  if (isNaN(d.getTime())) {
    return dateStr;
  }

  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/* ============================================================
   TIME FORMAT

   Supports:
   10:00
   10:00 - 11:00
   10:00 AM - 11:00 AM
============================================================ */

function formatTime12h(timeStr) {
  if (!timeStr) return "";

  const ampmMatches = [
    ...timeStr.matchAll(
      /(\d{1,2}:\d{2})\s*([APap][Mm])/g
    ),
  ];

  if (ampmMatches.length) {
    return ampmMatches
      .map(
        (m) =>
          `${m[1]} ${m[2].toUpperCase()}`
      )
      .join(" - ");
  }

  const parts = timeStr
    .split(/\s*-\s*/)
    .map((p) => p.trim())
    .filter(Boolean);

  const to12h = (time) => {
    const [hStr, mStr = "00"] =
      time.split(":");

    let h = parseInt(hStr, 10);

    if (isNaN(h)) {
      return time;
    }

    const meridiem =
      h >= 12 ? "PM" : "AM";

    h = h % 12;

    if (h === 0) {
      h = 12;
    }

    return `${h}:${mStr.padStart(
      2,
      "0"
    )} ${meridiem}`;
  };

  return parts.map(to12h).join(" - ");
}

/* ============================================================
   SESSION END DATE
============================================================ */

function sessionEndDate(dateStr, timeStr) {
  if (!dateStr || !timeStr) {
    return null;
  }

  const matches = [
    ...(timeStr || "").matchAll(
      /(\d{1,2}:\d{2}\s*[APap][Mm])/g
    ),
  ];

  /*
    If time is:
    10:00 AM - 11:00 AM

    use second time = 11:00 AM
  */

  const target =
    matches[1]?.[1] ||
    matches[0]?.[1];

  /*
    If API sends:
    10:00 - 11:00
  */

  if (!target) {
    const plainTimes = timeStr
      .split(/\s*-\s*/)
      .map((t) => t.trim())
      .filter(Boolean);

    const plainTarget =
      plainTimes[plainTimes.length - 1];

    if (!plainTarget) {
      return null;
    }

    const [hours, minutes] =
      plainTarget.split(":").map(Number);

    if (
      isNaN(hours) ||
      isNaN(minutes)
    ) {
      return null;
    }

    const d = new Date(dateStr);

    d.setHours(
      hours,
      minutes,
      0,
      0
    );

    return d;
  }

  const parts = target
    .trim()
    .split(/\s+/);

  const time = parts[0];

  const meridiem =
    parts[1]?.toUpperCase();

  let [hours, minutes] =
    time.split(":").map(Number);

  if (
    isNaN(hours) ||
    isNaN(minutes)
  ) {
    return null;
  }

  if (
    meridiem === "PM" &&
    hours !== 12
  ) {
    hours += 12;
  }

  if (
    meridiem === "AM" &&
    hours === 12
  ) {
    hours = 0;
  }

  const d = new Date(dateStr);

  d.setHours(
    hours,
    minutes,
    0,
    0
  );

  return d;
}

/* ============================================================
   CHECK SESSION EXPIRY
============================================================ */

function isSessionExpired(session) {
  const end = sessionEndDate(
    session?.date,
    session?.time
  );

  if (!end) {
    return false;
  }

  return end.getTime() < Date.now();
}

/* ============================================================
   SAFE JSON
============================================================ */

async function safeJson(res, url) {
  const text = await res.text();

  try {
    return JSON.parse(text);
  } catch (error) {
    console.error(
      `Non-JSON response from ${url}:`,
      text.slice(0, 500)
    );

    throw new Error(
      `Server returned an invalid response from:\n${url}`
    );
  }
}

/* ============================================================
   COUNTRIES
============================================================ */

const ALL_COUNTRIES =
  Country.getAllCountries()
    .filter(
      (country) => country.phonecode
    )
    .sort((a, b) =>
      a.name.localeCompare(b.name)
    );

const DEFAULT_COUNTRY =
  ALL_COUNTRIES.find(
    (country) =>
      country.isoCode === "IN"
  ) || ALL_COUNTRIES[0];

/* ============================================================
   COMPONENT
============================================================ */

const DemoSession = () => {
   const location = useLocation();
const navigate = useNavigate();
const { slug } = useParams();

const courseFromUrl = slug || "";

const courseFromState = location.state?.course || null;

const courseName =
  courseFromState?.title ||
  courseFromState?.name ||
  prettifySlug(courseFromUrl);

  /* ============================================================
     AUTH STATE
  ============================================================ */

  const [authChecked, setAuthChecked] =
    useState(false);

  const [isLoggedIn, setIsLoggedIn] =
    useState(false);

  /* ============================================================
     LOGIN MODAL STATE
  ============================================================ */

  const [showLoginModal, setShowLoginModal] =
    useState(false);

  const [userForm, setUserForm] =
    useState({
      name: "",
      email: "",
      phone: "",
      countryCode: "+91",
    });

  const [showOtp, setShowOtp] =
    useState(false);

  const [otp, setOtp] =
    useState("");

  const [seconds, setSeconds] =
    useState(120);

  const timerRef =
    useRef(null);

  /* ============================================================
     FORM
  ============================================================ */

  const [form, setForm] =
    useState({
      name: "",
      email: "",
      phone: "",
      countryIso:
        DEFAULT_COUNTRY?.isoCode || "IN",
      stateIso: "",
      city: "",
      course: courseName,
    });

  /* ============================================================
     SESSION STATES
  ============================================================ */

  const [sessions, setSessions] =
    useState([]);

  const [selectedSession, setSelectedSession] =
    useState(null);

  const [loadingSessions, setLoadingSessions] =
    useState(true);

  const [submitting, setSubmitting] =
    useState(false);

  /* ============================================================
     COUNTRY
  ============================================================ */

  const selectedCountry =
    useMemo(
      () =>
        ALL_COUNTRIES.find(
          (country) =>
            country.isoCode ===
            form.countryIso
        ) || null,
      [form.countryIso]
    );

  const dialCode =
    selectedCountry
      ? `+${selectedCountry.phonecode}`
      : "+91";

  /* ============================================================
     STATES
  ============================================================ */

  const states = useMemo(
    () =>
      form.countryIso
        ? State.getStatesOfCountry(
            form.countryIso
          )
        : [],
    [form.countryIso]
  );

  /* ============================================================
     CITIES
  ============================================================ */

  const cities = useMemo(
    () =>
      form.countryIso &&
      form.stateIso
        ? City.getCitiesOfState(
            form.countryIso,
            form.stateIso
          )
        : [],
    [
      form.countryIso,
      form.stateIso,
    ]
  );

  /* ============================================================
     LOAD USER PROFILE
  ============================================================ */

  const loadUserProfile = async (
    email
  ) => {
    if (!email) return;

    try {
      const fd = new FormData();

      fd.append(
        "email",
        email
      );

      const res = await fetch(
        `${API_BASE}/checkUser.php`,
        {
          method: "POST",
          body: fd,
        }
      );

      const data =
        await res.json();

      if (data?.exists) {
        const resolvedName =
          data.name ||
          data.full_name ||
          data.fullName ||
          data.username ||
          "";

        const resolvedPhone =
          data.phone ||
          data.mobile ||
          data.contact ||
          "";

        setUserForm(
          (previous) => ({
            ...previous,

            email:
              data.email ||
              email,

            name:
              resolvedName ||
              previous.name,

            phone:
              resolvedPhone ||
              previous.phone,
          })
        );

        setForm(
          (previous) => ({
            ...previous,

            email:
              data.email ||
              email,

            name:
              resolvedName ||
              previous.name,

            phone:
              resolvedPhone ||
              previous.phone,
          })
        );
      }
    } catch (error) {
      console.error(
        "Failed to load user profile:",
        error
      );
    }
  };

  /* ============================================================
     AUTH CHECK

     IMPORTANT:
     This is what was missing from the old DemoSession.jsx.

     If there is no email in localStorage:
     → Show login modal.

     If email exists:
     → Verify it with checkUser.php.
============================================================ */

  useEffect(() => {
    let cancelled = false;

    const checkAccess = async () => {
      const storedEmail =
        localStorage.getItem("email");

      /*
        NO LOGIN FOUND
      */

      if (!storedEmail) {
        if (!cancelled) {
          setIsLoggedIn(false);
          setShowLoginModal(true);
          setAuthChecked(true);
        }

        return;
      }

      /*
        VERIFY STORED EMAIL
      */

      try {
        const fd = new FormData();

        fd.append(
          "email",
          storedEmail
        );

        const res = await fetch(
          `${API_BASE}/checkUser.php`,
          {
            method: "POST",
            body: fd,
          }
        );

        const data =
          await res.json();

        if (cancelled) return;

        if (data?.exists) {
          /*
            Valid existing user
          */

          localStorage.setItem(
            "loggedIn",
            "true"
          );

          localStorage.setItem(
            "email",
            storedEmail
          );

          setIsLoggedIn(true);
          setShowLoginModal(false);

          await loadUserProfile(
            storedEmail
          );
        } else {
          /*
            Email no longer exists.
            Clear stale login.
          */

          localStorage.removeItem(
            "email"
          );

          localStorage.removeItem(
            "loggedIn"
          );

          setIsLoggedIn(false);
          setShowLoginModal(true);
        }
      } catch (error) {
        console.error(
          "Authentication check failed:",
          error
        );

        /*
          If network fails, do NOT
          silently grant access.

          Keep login gate visible.
        */

        if (!cancelled) {
          setIsLoggedIn(false);
          setShowLoginModal(true);
        }
      } finally {
        if (!cancelled) {
          setAuthChecked(true);
        }
      }
    };

    checkAccess();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ============================================================
     OTP TIMER
  ============================================================ */

  const startTimer = () => {
    clearInterval(
      timerRef.current
    );

    setSeconds(120);

    timerRef.current =
      setInterval(() => {
        setSeconds(
          (previous) => {
            if (previous <= 1) {
              clearInterval(
                timerRef.current
              );

              return 0;
            }

            return previous - 1;
          }
        );
      }, 1000);
  };

  /* ============================================================
     CLEANUP OTP TIMER
  ============================================================ */

  useEffect(() => {
    return () => {
      clearInterval(
        timerRef.current
      );
    };
  }, []);

  /* ============================================================
     INPUT CHANGE — LOGIN
  ============================================================ */

  const handleLoginInputChange = (
    e
  ) => {
    let {
      name,
      value,
    } = e.target;

    if (name === "name") {
      value = value
        .replace(
          /[^A-Za-z ]/g,
          ""
        )
        .replace(
          /\s+/g,
          " "
        )
        .replace(
          /^\s+/,
          ""
        );
    }

    if (name === "email") {
      value = value
        .replace(/\s/g, "")
        .toLowerCase();
    }

    if (name === "phone") {
      value = value
        .replace(/\D/g, "")
        .slice(0, 15);
    }

    setUserForm(
      (previous) => ({
        ...previous,
        [name]: value,
      })
    );
  };

  /* ============================================================
     SEND OTP
  ============================================================ */

  const handleSendOtp =
    async () => {
      const name =
        (
          userForm.name || ""
        ).trim();

      const email =
        (
          userForm.email || ""
        )
          .trim()
          .toLowerCase();

      const phoneRaw =
        String(
          userForm.phone || ""
        ).trim();

      const phoneDigits =
        phoneRaw.replace(
          /\D/g,
          ""
        );

      const dialDigits =
        (
          userForm.countryCode ||
          "+91"
        ).replace(
          /\D/g,
          ""
        );

      const localPhone =
        phoneDigits.startsWith(
          dialDigits
        )
          ? phoneDigits.slice(
              dialDigits.length
            )
          : phoneDigits;

      /* NAME */

      if (!name) {
        Swal.fire({
          icon: "warning",
          title: "Name required",
          text:
            "Please enter your full name.",
        });

        return;
      }

      /* EMAIL */

      const emailRegex =
        /^(?!.*\.\.)(?!.*@.*@)[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+\.[A-Za-z]{2,}$/;

      if (
        !email ||
        !emailRegex.test(email)
      ) {
        Swal.fire({
          icon: "warning",
          title: "Invalid email",
          text:
            "Please enter a valid email address.",
        });

        return;
      }

      /* PHONE */

      if (
        !localPhone ||
        localPhone.length < 8
      ) {
        Swal.fire({
          icon: "warning",
          title: "Phone required",
          text:
            "Please enter a valid phone number.",
        });

        return;
      }

      try {
        const fd =
          new FormData();

        fd.append(
          "name",
          name
        );

        fd.append(
          "email",
          email
        );

        fd.append(
          "phone",
          localPhone
        );

        fd.append(
          "countryCode",
          userForm.countryCode ||
            "+91"
        );

        const res =
          await fetch(
            `${API_BASE}/sendOtp.php`,
            {
              method: "POST",
              body: fd,
            }
          );

        const data =
          await res.json();

        /*
          Some versions of sendOtp.php
          return exists/alreadyRegistered.
        */

        if (
          data?.exists ||
          data?.alreadyRegistered
        ) {
          /*
            User already exists.

            We still allow them through
            after confirming that the
            email exists.
          */

          localStorage.setItem(
            "email",
            email
          );

          localStorage.setItem(
            "loggedIn",
            "true"
          );

          clearInterval(
            timerRef.current
          );

          setIsLoggedIn(true);
          setShowLoginModal(false);
          setShowOtp(false);

          await loadUserProfile(
            email
          );

          Swal.fire({
            icon: "success",
            title: "Welcome Back!",
            text:
              "You are already registered.",
            timer: 1500,
            showConfirmButton: false,
          });

          return;
        }

        if (data?.success) {
          setUserForm(
            (previous) => ({
              ...previous,
              email,
            })
          );

          setShowOtp(true);

          startTimer();

          Swal.fire({
            icon: "success",
            title: "OTP Sent!",
            text:
              `OTP sent to ${email}.`,
            timer: 2500,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed",
            text:
              data?.message ||
              "Could not send OTP.",
          });
        }
      } catch (error) {
        console.error(
          "Send OTP error:",
          error
        );

        Swal.fire({
          icon: "error",
          title: "Network error",
          text:
            "Please check your connection.",
        });
      }
    };

  /* ============================================================
     VERIFY OTP
  ============================================================ */

  const handleVerifyOtp =
    async () => {
      const cleanOtp =
        String(otp || "")
          .replace(/\D/g, "")
          .slice(0, 6);

      if (
        cleanOtp.length !== 6
      ) {
        Swal.fire({
          icon: "warning",
          title: "Invalid OTP",
          text:
            "Please enter a valid 6-digit OTP.",
        });

        return;
      }

      const email =
        (
          userForm.email || ""
        )
          .trim()
          .toLowerCase();

      try {
        const fd =
          new FormData();

        fd.append(
          "email",
          email
        );

        fd.append(
          "otp",
          cleanOtp
        );

        const res =
          await fetch(
            `${API_BASE}/verifyOtp.php`,
            {
              method: "POST",
              body: fd,
            }
          );

        const data =
          await res.json();

        if (data?.success) {
          /*
            SAVE LOGIN
          */

          localStorage.setItem(
            "loggedIn",
            "true"
          );

          localStorage.setItem(
            "email",
            email
          );

          localStorage.setItem(
            "justVerified",
            "true"
          );

          clearInterval(
            timerRef.current
          );

          /*
            CLOSE LOGIN
          */

          setShowLoginModal(false);
          setShowOtp(false);
          setIsLoggedIn(true);

          /*
            Copy user data into
            demo registration form.
          */

          setForm(
            (previous) => ({
              ...previous,

              name:
                userForm.name ||
                previous.name,

              email,

              phone:
                userForm.phone ||
                previous.phone,

              course:
                courseName,
            })
          );

          Swal.fire({
            icon: "success",
            title: "Verified!",
            text:
              "Welcome! You now have access to the demo session.",
            timer: 2000,
            showConfirmButton: false,
          });

          /*
            Refresh profile from database.
          */

          await loadUserProfile(
            email
          );
        } else {
          Swal.fire({
            icon: "error",
            title: "Invalid OTP",
            text:
              data?.message ||
              "The OTP you entered is incorrect.",
          });
        }
      } catch (error) {
        console.error(
          "OTP verification error:",
          error
        );

        Swal.fire({
          icon: "error",
          title: "Network error",
          text:
            "Please check your connection.",
        });
      }
    };

  /* ============================================================
     RESEND OTP
  ============================================================ */

  const handleResend =
    async () => {
      setOtp("");
      setShowOtp(false);

      await handleSendOtp();
    };

  /* ============================================================
     LOGIN MODAL CLOSE

     User cannot bypass login.
  ============================================================ */

  const handleLoginClose =
    () => {
      if (isLoggedIn) {
        setShowLoginModal(false);
      }
    };

  /* ============================================================
     LOAD DEMO SESSIONS
  ============================================================ */

  const fetchSessions =
    async () => {
      try {
        setLoadingSessions(true);

        /*
          IMPORTANT:

          Use the URL slug directly.

          Example:
          ai-ml-with-applied-gen-ai
        */

        if (!courseFromUrl) {
          console.error(
            "Demo session course slug is missing."
          );

          setSessions([]);

          return;
        }

        const url =
          `${API_BASE}/getDemoSessions.php?course=` +
          encodeURIComponent(
            courseFromUrl
          );

        console.log(
          "Demo Session Course:",
          courseFromUrl
        );

        console.log(
          "Demo Session API URL:",
          url
        );

        const res =
          await fetch(url);

        const data =
          await safeJson(
            res,
            url
          );

        console.log(
          "Demo Session API Response:",
          data
        );

        if (data?.success) {
          const available =
            (
              data.sessions || []
            ).filter(
              (session) =>
                !isSessionExpired(
                  session
                )
            );

          setSessions(
            available
          );
        } else {
          console.error(
            "Demo sessions API error:",
            data?.message
          );

          setSessions([]);
        }
      } catch (error) {
        console.error(
          "Failed to load demo sessions:",
          error
        );

        setSessions([]);
      } finally {
        setLoadingSessions(false);
      }
    };

  /*
    Only load demo sessions once
    the authentication check is
    complete AND user is logged in.
  */

  useEffect(() => {
    if (
      !authChecked ||
      !isLoggedIn ||
      !courseFromUrl
    ) {
      return;
    }

    fetchSessions();
  }, [
    authChecked,
    isLoggedIn,
    courseFromUrl,
  ]);

  /* ============================================================
     REMOVE EXPIRED SESSIONS AUTOMATICALLY
  ============================================================ */

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const interval =
      setInterval(() => {
        setSessions(
          (previous) => {
            const validSessions =
              previous.filter(
                (session) =>
                  !isSessionExpired(
                    session
                  )
              );

            if (
              validSessions.length !==
              previous.length
            ) {
              setSelectedSession(
                (selected) => {
                  if (
                    selected &&
                    isSessionExpired(
                      selected
                    )
                  ) {
                    return null;
                  }

                  return selected;
                }
              );
            }

            return validSessions;
          }
        );
      }, 30000);

    return () =>
      clearInterval(interval);
  }, [isLoggedIn]);

  /* ============================================================
     INPUT CHANGE — DEMO FORM
  ============================================================ */

  const handleChange = (
    e
  ) => {
    let {
      name,
      value,
    } = e.target;

    /* NAME */

    if (name === "name") {
      value = value
        .replace(
          /^\s+/,
          ""
        )
        .replace(
          /[^A-Za-z ]/g,
          ""
        )
        .replace(
          /\s{2,}/g,
          " "
        );
    }

    /* PHONE */

    if (name === "phone") {
      value = value
        .replace(/\D/g, "")
        .slice(0, 15);
    }

    setForm(
      (previous) => ({
        ...previous,
        [name]: value,
      })
    );
  };

  /* ============================================================
     COUNTRY CHANGE
  ============================================================ */

  const handleCountryChange =
    (e) => {
      const isoCode =
        e.target.value;

      setForm(
        (previous) => ({
          ...previous,

          countryIso:
            isoCode,

          stateIso: "",

          city: "",
        })
      );
    };

  /* ============================================================
     STATE CHANGE
  ============================================================ */

  const handleStateChange =
    (e) => {
      const isoCode =
        e.target.value;

      setForm(
        (previous) => ({
          ...previous,

          stateIso:
            isoCode,

          city: "",
        })
      );
    };

  /* ============================================================
     CITY CHANGE
  ============================================================ */

  const handleCityChange =
    (e) => {
      setForm(
        (previous) => ({
          ...previous,

          city:
            e.target.value,
        })
      );
    };

  /* ============================================================
     SUBMIT
  ============================================================ */

  const handleSubmit =
    async (e) => {
      e.preventDefault();

      if (submitting) {
        return;
      }

      /* NAME */

      if (!form.name.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Name required",
          text:
            "Please enter your full name.",
        });

        return;
      }

      /* EMAIL */

      if (!form.email.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Email required",
          text:
            "Please enter your email address.",
        });

        return;
      }

      /* PHONE */

      if (
        form.phone.length < 8
      ) {
        Swal.fire({
          icon: "warning",
          title: "Phone required",
          text:
            "Please enter a valid phone number.",
        });

        return;
      }

      /* COURSE SLUG */

      if (!courseFromUrl) {
        Swal.fire({
          icon: "error",
          title: "Course not found",
          text:
            "The demo course could not be identified.",
        });

        return;
      }

      /* SESSION */

      if (!selectedSession) {
        Swal.fire({
          icon: "warning",
          title:
            "Select a demo slot",
          text:
            "Please choose a demo date and time.",
        });

        return;
      }

      /* CHECK EXPIRY */

      if (
        isSessionExpired(
          selectedSession
        )
      ) {
        Swal.fire({
          icon: "info",
          title:
            "Slot No Longer Available",
          text:
            "That session has already ended. Please pick another slot.",
        });

        setSelectedSession(
          null
        );

        setSessions(
          (previous) =>
            previous.filter(
              (session) =>
                !isSessionExpired(
                  session
                )
            )
        );

        return;
      }

      setSubmitting(true);

      const url =
        `${API_BASE}/submitDemoRegistration.php`;

      const stateObj =
        states.find(
          (state) =>
            state.isoCode ===
            form.stateIso
        );

      try {
        const fd =
          new FormData();

        fd.append(
          "name",
          form.name
        );

        fd.append(
          "email",
          form.email
        );

        fd.append(
          "phone",
          form.phone
        );

        fd.append(
          "countryCode",
          dialCode
        );

        fd.append(
          "country",
          selectedCountry?.name ||
            ""
        );

        fd.append(
          "state",
          stateObj?.name ||
            ""
        );

        fd.append(
          "city",
          form.city
        );

        /*
          Human readable course name
        */

        fd.append(
          "course",
          form.course
        );

        /*
          IMPORTANT:

          Send actual slug as well.
        */

        fd.append(
          "courseSlug",
          courseFromUrl
        );

        fd.append(
          "sessionId",
          selectedSession.id
        );

        fd.append(
          "sessionDate",
          selectedSession.date
        );

        fd.append(
          "sessionTime",
          selectedSession.time
        );

        console.log(
          "Submitting demo registration:",
          {
            name: form.name,
            email: form.email,
            phone: form.phone,
            country:
              selectedCountry?.name,
            state:
              stateObj?.name,
            city: form.city,
            course:
              form.course,
            courseSlug:
              courseFromUrl,
            sessionId:
              selectedSession.id,
            sessionDate:
              selectedSession.date,
            sessionTime:
              selectedSession.time,
          }
        );

        const res =
          await fetch(
            url,
            {
              method: "POST",
              body: fd,
            }
          );

        const data =
          await safeJson(
            res,
            url
          );

        if (data?.success) {
          await Swal.fire({
            icon: "success",
            title:
              "Demo Session Booked!",
            html: `
              Your demo session has been registered successfully.
              <br/><br/>
              A confirmation email with your demo link has been sent to
              <b>${form.email}</b>.
              <br/><br/>
              <span style="color:#b45309;">
                Don't see it in your inbox?
                Please check your
                <b>Spam / Junk folder</b>.
              </span>
            `,
            confirmButtonColor:
              "#2563eb",
          });

          navigate("/");
        } else {
          const rawMsg =
            data?.message || "";

          const isDuplicate =
            /already registered/i.test(
              rawMsg
            );

          const isSlotIssue =
            /slot is full/i.test(
              rawMsg
            ) ||
            /no longer available/i.test(
              rawMsg
            ) ||
            /already passed/i.test(
              rawMsg
            );

          if (isDuplicate) {
            Swal.fire({
              icon: "info",
              title:
                "You're Already Registered",
              text:
                "You've already booked this date & time slot. Please pick a different slot.",
              confirmButtonText:
                "Choose Another Slot",
            });

            setSelectedSession(
              null
            );
          } else if (
            isSlotIssue
          ) {
            Swal.fire({
              icon: "warning",
              title:
                "Slot Unavailable",
              text:
                rawMsg ||
                "This slot is no longer available.",
              confirmButtonText:
                "Choose Another Slot",
            });

            setSelectedSession(
              null
            );

            setSessions(
              (previous) =>
                previous.filter(
                  (session) =>
                    session.id !==
                    selectedSession.id
                )
            );
          } else {
            Swal.fire({
              icon: "warning",
              title:
                "One More Thing...",
              text:
                rawMsg ||
                "We couldn't complete your registration. Please check your details and try again.",
            });
          }
        }
      } catch (error) {
        console.error(
          "Demo registration error:",
          error
        );

        Swal.fire({
          icon: "error",
          title:
            "Something went wrong",
          text:
            error.message ||
            "Please check your connection and try again.",
        });
      } finally {
        setSubmitting(false);
      }
    };

  /* ============================================================
     AUTH LOADING
  ============================================================ */

  if (!authChecked) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          color: "#374151",
        }}
      >
        Loading...
      </div>
    );
  }

  /* ============================================================
     LOGIN GATE

     IMPORTANT:
     Direct URL in another browser will come here
     because there will be no localStorage email.
  ============================================================ */

  if (!isLoggedIn) {
    return (
      <>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(135deg, #0f1740 0%, #1a2e80 100%)",
            position: "relative",
          }}
        >
          <h2
            style={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 24,
              marginBottom: 10,
              textAlign: "center",
            }}
          >
            Sign in to book your free demo
          </h2>

          <p
            style={{
              color:
                "rgba(255,255,255,0.65)",
              fontSize: 14,
              textAlign: "center",
              margin: 0,
            }}
          >
            Verify your email to continue
            to the demo session.
          </p>
        </div>

        <StaticBackdrop />

        <LoginModal
          showOtp={showOtp}
          userForm={userForm}
          setUserForm={setUserForm}
          otp={otp}
          setOtp={setOtp}
          seconds={seconds}
          onClose={
            handleLoginClose
          }
          onSendOtp={
            handleSendOtp
          }
          onVerifyOtp={
            handleVerifyOtp
          }
          onResend={
            handleResend
          }
        />
      </>
    );
  }

  /* ============================================================
     MAIN PAGE
  ============================================================ */

  return (
    <>
    <Helmet>

  <title>
    Join Free Demo - {courseName} | SWIFTIntelli
  </title>

  <meta
    name="description"
    content={`Join a free demo session for ${courseName} at SWIFTIntelli. Explore the course, learning experience, and practical training approach.`}
  />

</Helmet>
      <Navbar />

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

       <header className="blog-masthead">
              <div className="blog-masthead-overlay"></div>
      
           <div className="blog-masthead-content">
 <div className="blog-breadcrumb">

  <Link to="/">Home</Link>

  <span>/</span>

  <Link to="/courses">
    Courses
  </Link>

  <span>/</span>

  <Link to={`/courses/${courseFromUrl}`}>
    {courseName}
  </Link>

  <span>/</span>

  <span>Demo Session</span>

</div>

 <h1>
  Demo Session - {courseName}
</h1>
</div>
            </header>

      {/* =====================================================
          DEMO PAGE
      ===================================================== */}

      <main className="demo-page">
        <div className="demo-layout">

          {/* =================================================
              LEFT INFORMATION
          ================================================= */}

          <aside className="demo-info">

            <span className="demo-label">
              FREE DEMO
            </span>

            <h2>
              See how
              <span>
                {" "}SWIFTIntelli{" "}
              </span>
              can help you learn.
            </h2>

            <p>
              Join a live demo session
              and get a better understanding
              of our courses, teaching
              approach, practical projects,
              and learning experience.
            </p>

            <div className="demo-benefits">

              <div className="demo-benefit">
                <span>
                  01
                </span>

                <div>
                  <h3>
                    Live Learning Experience
                  </h3>

                  <p>
                    Get a feel for our practical
                    and industry-oriented
                    learning approach.
                  </p>
                </div>
              </div>

              <div className="demo-benefit">
                <span>
                  02
                </span>

                <div>
                  <h3>
                    Ask Your Questions
                  </h3>

                  <p>
                    Interact with our trainers
                    and clarify your
                    course-related questions.
                  </p>
                </div>
              </div>

              <div className="demo-benefit">
                <span>
                  03
                </span>

                <div>
                  <h3>
                    Choose Your Slot
                  </h3>

                  <p>
                    Select a convenient
                    available demo session
                    from the options.
                  </p>
                </div>
              </div>

            </div>

          </aside>

          {/* =================================================
              FORM
          ================================================= */}

          <section className="demo-form-card">

            <div className="demo-form-header">

              <span>
                GET STARTED
              </span>

              <h2>
                Reserve your demo
              </h2>

              <p>
                Fill in your details and select
                an available session.
              </p>

            </div>

            <form
              className="demo-form"
              onSubmit={handleSubmit}
            >

              {/* =================================================
                  NAME + EMAIL
              ================================================= */}

              <div className="demo-form-grid">

                <div className="demo-field">

                  <label>
                    Full Name{" "}
                    <b>*</b>
                  </label>

                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={
                      handleChange
                    }
                    placeholder="Enter your full name"
                    required
                  />

                </div>

                <div className="demo-field">

                  <label>
                    Email Address{" "}
                    <b>*</b>
                  </label>

                  <div
                    style={{
                      position:
                        "relative",
                    }}
                  >

                    <input
                      type="email"
                      name="email"
                      value={
                        form.email
                      }
                      readOnly
                      disabled
                      className="demo-locked"
                      placeholder="you@example.com"
                      style={{
                        paddingRight:
                          "100px",
                      }}
                      required
                    />

                    {form.email && (
                      <span
                        style={{
                          position:
                            "absolute",
                          right:
                            "14px",
                          top:
                            "50%",
                          transform:
                            "translateY(-50%)",
                          color:
                            "#16a34a",
                          fontSize:
                            "13px",
                          fontWeight:
                            "600",
                          pointerEvents:
                            "none",
                        }}
                      >
                        ✓ Verified
                      </span>
                    )}

                  </div>

                </div>

              </div>

              {/* =================================================
                  PHONE + COUNTRY
              ================================================= */}

              <div className="demo-form-grid">

                <div className="demo-field">

                  <label>
                    Country{" "}
                    <b>*</b>
                  </label>

                  <select
                    name="countryIso"
                    value={
                      form.countryIso
                    }
                    onChange={
                      handleCountryChange
                    }
                    required
                  >

                    {ALL_COUNTRIES.map(
                      (country) => (
                        <option
                          key={
                            country.isoCode
                          }
                          value={
                            country.isoCode
                          }
                        >
                          {country.flag}{" "}
                          {country.name}{" "}
                          (+
                          {
                            country.phonecode
                          }
                          )
                        </option>
                      )
                    )}

                  </select>

                </div>

                <div className="demo-field">

                  <label>
                    Phone Number{" "}
                    <b>*</b>
                  </label>

                  <div className="demo-phone">

                    <span>
                      {dialCode ||
                        "+91"}
                    </span>

                    <input
                      type="tel"
                      name="phone"
                      value={
                        form.phone
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="Phone number"
                      required
                    />

                  </div>

                </div>

              </div>

              {/* =================================================
                  STATE + CITY
              ================================================= */}

              <div className="demo-form-grid">

                <div className="demo-field">

                  <label>
                    State
                  </label>

                  <select
                    name="stateIso"
                    value={
                      form.stateIso
                    }
                    onChange={
                      handleStateChange
                    }
                    disabled={
                      !states.length
                    }
                  >

                    <option value="">
                      {states.length
                        ? "Select state"
                        : "No states available"}
                    </option>

                    {states.map(
                      (state) => (
                        <option
                          key={
                            state.isoCode
                          }
                          value={
                            state.isoCode
                          }
                        >
                          {state.name}
                        </option>
                      )
                    )}

                  </select>

                </div>

                <div className="demo-field">

                  <label>
                    City
                  </label>

                  <select
                    name="city"
                    value={
                      form.city
                    }
                    onChange={
                      handleCityChange
                    }
                    disabled={
                      !cities.length
                    }
                  >

                    <option value="">
                      {cities.length
                        ? "Select city"
                        : "No cities available"}
                    </option>

                    {cities.map(
                      (city) => (
                        <option
                          key={`${city.name}-${city.latitude}-${city.longitude}`}
                          value={
                            city.name
                          }
                        >
                          {city.name}
                        </option>
                      )
                    )}

                  </select>

                </div>

              </div>

              {/* =================================================
                  COURSE
              ================================================= */}

              <div className="demo-field">

                <label>
                  Course
                </label>

                <input
                  type="text"
                  name="course"
                  value={
                    form.course
                  }
                  readOnly
                  disabled
                  className="demo-locked"
                  placeholder="Course name"
                />

              </div>

              {/* =================================================
                  DEMO SESSIONS
              ================================================= */}

              <div className="demo-field">

                <label>
                  Select Demo Date & Time{" "}
                  <b>*</b>
                </label>

                {loadingSessions ? (

                  <div className="demo-loading">
                    Loading available
                    slots...
                  </div>

                ) : sessions.length ===
                  0 ? (

                  <div className="demo-coming">

                    <strong>
                      New Demo Session Coming Soon
                    </strong>

                    <span>
                      Please check back shortly
                      for available sessions.
                    </span>

                  </div>

                ) : (

                  <div className="demo-sessions">

                    {sessions.map(
                      (session) => {

                        const expired =
                          isSessionExpired(
                            session
                          );

                        return (
                          <label
                            key={
                              session.id
                            }
                            className={`
                              demo-session
                              ${
                                selectedSession?.id ===
                                session.id
                                  ? "selected"
                                  : ""
                              }
                              ${
                                expired
                                  ? "disabled"
                                  : ""
                              }
                            `}
                          >

                            <input
                              type="radio"
                              name="demoSession"
                              checked={
                                selectedSession?.id ===
                                session.id
                              }
                              disabled={
                                expired
                              }
                              onChange={() =>
                                !expired &&
                                setSelectedSession(
                                  session
                                )
                              }
                            />

                            <div>

                              <strong>
                                {formatDateDMY(
                                  session.date
                                )}
                              </strong>

                              <span>
                                {formatTime12h(
                                  session.time
                                )}
                              </span>

                              {expired && (
                                <small>
                                  Completed
                                </small>
                              )}

                            </div>

                          </label>
                        );
                      }
                    )}

                  </div>

                )}

              </div>

              {/* =================================================
                  SUBMIT
              ================================================= */}

              <button
                type="submit"
                className="demo-submit"
                disabled={
                  submitting ||
                  sessions.length ===
                    0
                }
              >
                {submitting
                  ? "Submitting..."
                  : "Confirm Demo Session"}
              </button>

            </form>

          </section>

        </div>
      </main>
    <ChatbotWidget/>
      <Footer />
    </>
  );
};

export default DemoSession;